import React, { useState, useRef, useEffect } from 'react'
import { Link, useNavigate } from 'react-router-dom'
import { 
  BarChart3, 
  TrendingUp, 
  Menu,
  Home,
  User,
  Settings,
  LogOut,
  ChevronDown
} from 'lucide-react'

import { useAuth0 } from '@auth0/auth0-react'

/**
 * Header component with mobile-first responsive design
 * Includes mobile menu toggle and responsive navigation
 * Can show mobile sidebar button when used in AppLayout context
 */
const Header = ({ 
  appLayoutPage = false, 
  showMobileSidebarButton = false, 
  onMobileSidebarToggle 
}) => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false)
  const [isUserDropdownOpen, setIsUserDropdownOpen] = useState(false)
  const dropdownRef = useRef(null)

  // Navigation items matching the sidebar
  const navigationItems = [
    { name: 'Home', path: '/', icon: Home },
    { name: 'Dashboard', path: '/dashboard', icon: BarChart3 },
    { name: 'Portfolio', path: '/portfolio', icon: TrendingUp },
    // { name: 'Documents', path: '/documents', icon: FileText },
    // { name: 'Settings', path: '/settings', icon: Settings },
    // { name: 'Help', path: '/help', icon: HelpCircle },
  ]

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen)
  }

  const navigate = useNavigate()
  const { isAuthenticated, user, logout, isLoading } = useAuth0()

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setIsUserDropdownOpen(false)
      }
    }

    document.addEventListener('mousedown', handleClickOutside)
    return () => {
      document.removeEventListener('mousedown', handleClickOutside)
    }
  }, [])

  const handleLogin = () => {
    navigate('/login')
  }

  const handleLogout = () => {
    logout({ logoutParams: { returnTo: window.location.origin } })
  }

  const handleSettingsClick = () => {
    navigate('/settings')
    setIsUserDropdownOpen(false)
  }

  const toggleUserDropdown = () => {
    setIsUserDropdownOpen(!isUserDropdownOpen)
  }

  return (
    <header className="sticky top-0 z-40 bg-white shadow-sm border-b border-gray-200">
      <div className="container mx-auto px-4">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <div className="flex-shrink-0 md:hidden">
            <Link to="/dashboard" className="flex items-center space-x-2 text-xl font-bold text-gray-900">
              <img 
                src="https://startup-tnt-documents.tor1.digitaloceanspaces.com/public/startuptnt-icon.svg" 
                alt="Startup TNT Icon" 
                className="h-6 w-6"
              />
              <span>Angel Vault</span>
            </Link>
          </div>

          {/* Mobile menu button - shows different buttons based on context */}
          <div className="md:hidden">
            {showMobileSidebarButton ? (
              // Mobile sidebar button for AppLayout
              <button
                onClick={onMobileSidebarToggle}
                className="text-gray-700 hover:text-gray-900 p-2 rounded-md"
                aria-label="Open mobile sidebar"
              >
                <Menu className="h-6 w-6" />
              </button>
            ) : !appLayoutPage ? (
              // Regular mobile menu button for public pages
              <button
                onClick={toggleMobileMenu}
                className="text-gray-700 hover:text-gray-900 p-2 rounded-md"
                aria-label="Toggle mobile menu"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  {isMobileMenuOpen ? (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                  ) : (
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  )}
                </svg>
              </button>
            ) : null}
          </div>
          {/* User Menu / Auth - Right aligned on desktop/tablet */}
          <div className="hidden md:flex items-center ml-auto">
            {isAuthenticated ? (
              <div className="relative" ref={dropdownRef}>
                {/* User profile button - clickable to open dropdown */}
                <button
                  onClick={toggleUserDropdown}
                  className="flex items-center space-x-3 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 rounded-md transition-colors duration-200"
                >
                  {/* Auth0 user avatar or default user icon */}
                  {user?.picture ? (
                    <img
                      src={user.picture}
                      alt="User avatar"
                      className="h-8 w-8 rounded-full"
                    />
                  ) : (
                    <div className="h-8 w-8 rounded-full bg-gray-300 flex items-center justify-center">
                      <User className="h-5 w-5 text-gray-600" />
                    </div>
                  )}
                  {/* User name if available */}
                  {user?.name && (
                    <span className="font-medium">{user.name}</span>
                  )}
                  {/* Dropdown chevron */}
                  <ChevronDown 
                    className={`h-4 w-4 text-gray-500 transition-transform duration-200 ${
                      isUserDropdownOpen ? 'rotate-180' : ''
                    }`} 
                  />
                </button>

                {/* Dropdown menu */}
                {isUserDropdownOpen && (
                  <div className="absolute right-0 mt-2 w-48 bg-white rounded-md shadow-lg border border-gray-200 py-1 z-50">
                    {/* Settings option */}
                    <button
                      onClick={handleSettingsClick}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                    >
                      <Settings className="h-4 w-4 mr-3 text-gray-500" />
                      Settings
                    </button>
                    
                    {/* Divider */}
                    <div className="border-t border-gray-100 my-1"></div>
                    
                    {/* Logout option */}
                    <button
                      onClick={handleLogout}
                      className="flex items-center w-full px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 transition-colors duration-200"
                    >
                      <LogOut className="h-4 w-4 mr-3 text-gray-500" />
                      Log out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <button 
                onClick={handleLogin}
                className="bg-tnt-default hover:bg-tnt-hover text-white px-4 py-2 rounded-md text-sm font-medium">
                Sign In
              </button>
            )}
          </div>
        </div>        
      </div>
    </header>
  )
}

export default Header
